import { Icon } from '@zillow/constellation-10';
import { IconBuildingOutline } from '@zillow/constellation-icons';
import {
  StyledAdCard,
  StyledAdHeader,
  StyledAdLink,
  StyledAdSubHeader,
  StyledBrandImage,
  StyledFeesDisclaimer,
} from './StyledComponents';
import { styled } from '@/styled-system/jsx';
import { urlUtils } from '@zg-rentals/rental-manager-env';

const StyledHeadingLine = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '8px',
    '& > div:first-child': {
      fontSize: '22px',
    },
  },
});

const StyledBoldSubHeader = styled('div', {
  base: {
    fontSize: '16px',
    fontWeight: '700',
    lineHeight: '22px',
    color: '#2a2a33',
    paddingTop: '2px',
  },
});

const StyledSrpTextContent = styled('div', {
  base: {
    padding: '8px 12px 6px',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    '& > div': {
      fontSize: '16px',
    },
  },
});

const StyledBadge = styled('div', {
  base: {
    color: 'white',
    fontWeight: '800',
    fontSize: '14px',
    lineHeight: '16px',
    padding: '4px 10px',
    borderRadius: '4px',
    background: 'rgba(0, 0, 0, 0.54)',
    whiteSpace: 'nowrap',
  },
});

const StyledCtaRow = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    width: '100%',
    padding: '8px 0',
    marginTop: '6px',
    color: 'white',
    backgroundColor: '#006AFF',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: '900',
  },
});

export interface DfpPreviewSrpNativeProps {
  templateValues: Record<string, string>;
}

// Keep in sync with GAM creative template: Rentals SRP Native (ID 12488062)
// Reference: Zillow Responsive Web SRP|Search|Class_1v7_1
export const DfpPreviewSrpNative = ({ templateValues }: DfpPreviewSrpNativeProps) => {
  const href = templateValues.URLClickthroughZillowWeb
    ? urlUtils.addProtocol(templateValues.URLClickthroughZillowWeb)
    : templateValues.DestinationUrl
      ? urlUtils.addProtocol(templateValues.DestinationUrl)
      : '#';

  const flagLabel = templateValues.CustomCopy || templateValues.ListFlagOptions;

  const bedsItems = [
    templateValues.Subheader01Beds && { value: templateValues.Subheader01Beds, label: ' bd' },
    templateValues.Subheader01Baths && { value: templateValues.Subheader01Baths, label: ' ba' },
    templateValues.Subheader01SqFt && { value: templateValues.Subheader01SqFt, label: ' sqft' },
  ].filter(Boolean) as Array<{ value: string; label: string }>;
  const hasBedsLine = bedsItems.length > 0;

  const streetAddress = [templateValues.AddressPart1, templateValues.AddressPart2].filter(Boolean).join(' ');
  const addressLine = [templateValues.BuildingName, streetAddress].filter(Boolean).join(' | ');

  return (
    <div className="DfpPreviewSrpNative">
      <div className="container">
        <StyledAdCard>
          <StyledAdLink href={href} target="_blank" rel="noopener noreferrer" id="clickthru">
            <StyledBrandImage style={{ backgroundImage: `url(${templateValues.ImageBrandRectangle})` }}>
              <div style={{ position: 'absolute', top: '8px', left: '8px', display: 'flex', gap: '4px' }}>
                <StyledBadge>{templateValues.TextSlug || 'Sponsored'}</StyledBadge>
                {flagLabel && <StyledBadge style={{ background: '#006AFF' }}>{flagLabel}</StyledBadge>}
              </div>
            </StyledBrandImage>
            <StyledSrpTextContent>
              <StyledHeadingLine>
                <StyledAdHeader>
                  {[templateValues.PriceHeading, templateValues.UnitHeading].filter(Boolean).join(' ')}
                </StyledAdHeader>
                {templateValues.FeesDisclaimer && (
                  <StyledFeesDisclaimer>{templateValues.FeesDisclaimer}</StyledFeesDisclaimer>
                )}
              </StyledHeadingLine>
              {(hasBedsLine || templateValues.ForRentTag) && (
                <StyledAdSubHeader>
                  {bedsItems.map((item, i) => (
                    <span key={i}>
                      {i > 0 && ' | '}
                      <strong>{item.value}</strong>
                      {item.label}
                    </span>
                  ))}
                  {templateValues.ForRentTag && `${hasBedsLine ? ' | ' : ''}${templateValues.ForRentTag}`}
                </StyledAdSubHeader>
              )}
              {addressLine && <StyledAdSubHeader>{addressLine}</StyledAdSubHeader>}
              <StyledCtaRow>
                <Icon>
                  <IconBuildingOutline />
                </Icon>
                {templateValues.TextCTA || 'Learn more'}
              </StyledCtaRow>
            </StyledSrpTextContent>
          </StyledAdLink>
        </StyledAdCard>
      </div>
    </div>
  );
};

export default DfpPreviewSrpNative;
